import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { LanguageModel } from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Link, { LinkPresentationProps } from "@waytek/MobiusOverrides/Link/Link";
import { extPrice } from "@waytek/Utilities/global/products";
import { getCurrentUom } from "@waytek/Utilities/global/utils";
import { colorPalette } from "@waytek/Utilities/variables";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: CustomBreakPricingStyles;
    language: LanguageModel | null;
    currencySymbol: string | undefined;
    maxRows: number;
}

export interface CustomBreakPricingStyles {
    wrapper?: InjectableCss;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    headerQty?: DataTableHeaderProps;
    headereUnitPrice?: DataTableHeaderProps;
    headerExtPrice?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    cellQty?: DataTableCellBaseProps;
    cellPrice?: DataTableCellBaseProps;
    cellExtPrice?: DataTableCellBaseProps;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

const headerBasic: DataTableHeaderProps = {
    alignX: "right",
    typographyProps: { size: 14, weight: 600, lineHeight: 1.71, color: colorPalette.common.global.secondaryGray },
    css: css`
        && {
            color: ${colorPalette.common.global.secondaryGray};
            border-bottom: 1px solid ${colorPalette.common.global.lightGray};
            background: none;
            height: auto;
            padding: 0 3px;
            font-size: 14px;
            font-weight: 600;
            line-height: 1.71;
        }
    `,
};

const cellBasic: DataTableCellBaseProps = {
    alignX: "right",
    css: css`
        &&& {
            font-size: 12px;
            font-weight: normal;
            line-height: 2;
            border-bottom: 1px solid ${colorPalette.common.global.lightGray};
            height: auto;
            padding: 0 3px;

            ${TypographyStyle} {
                font: inherit;
                line-height: inherit;
            }
        }
    `,
};

export const customBreakPricingStyles: CustomBreakPricingStyles = {
    wrapper: {
        css: css`
            margin-bottom: 18px;
        `,
    },
    title: {
        size: 14,
        weight: 600,
        lineHeight: 1.71,
        css: css`
            margin: 0;
            margin-top: 5px;
        `,
    },
    table: {
        css: css`
            margin: 0;
        `,
    },
    headerQty: {
        ...headerBasic,
        alignX: "center",
    },
    headereUnitPrice: {
        ...headerBasic,
    },
    headerExtPrice: {
        ...headerBasic,
    },
    tableRow: {
        css: css`
            && {
                background: none;

                &:nth-child(even) {
                    background: none;
                }
            }
        `,
    },
    cellQty: {
        ...cellBasic,
        alignX: "center",
        css: css`
            ${cellBasic?.css}

            &&&& {
                font-size: 13px;
                line-height: 1.85;
            }
        `,
    },
    cellPrice: {
        ...cellBasic,
    },
    cellExtPrice: {
        ...cellBasic,
    },
    link: {
        css: css`
            margin: 0;
            margin-top: 10px;
        `,
    },
    tableBody: {
        css: css`
            && {
                border: 0;
            }
        `,
    },
    tableHead: {
        css: css`
            && {
                border: 0;
            }
        `,
    },
};

const CustomBreakPricing: FC<OwnProps & HasProduct> = ({
    product,
    productInfo: { pricing },
    extendedStyles,
    language,
    currencySymbol,
    maxRows = 2,
}) => {
    const [styles] = React.useState(() => mergeToNew(customBreakPricingStyles, extendedStyles));
    const [showAll, setShowAll] = React.useState(false);

    const toggleShowAll = () => {
        setShowAll(!showAll);
    };

    if (
        !language ||
        !currencySymbol ||
        !product ||
        product.quoteRequired ||
        !pricing ||
        !pricing.unitRegularBreakPrices ||
        pricing.unitRegularBreakPrices.length < 2
    ) {
        return null;
    }

    const unitOfMeasureText = getCurrentUom(product);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title}>
                {translate("Price Break per")} {unitOfMeasureText}
            </Typography>

            <DataTable {...styles.table}>
                <DataTableHead {...styles.tableHead}>
                    <DataTableHeader {...styles.headerQty}>{translate("Qty")}</DataTableHeader>
                    <DataTableHeader {...styles.headereUnitPrice}>{translate("Ea. Price")}</DataTableHeader>
                    <DataTableHeader {...styles.headerExtPrice}>{translate("Ext. Price")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.tableBody}>
                    {pricing?.unitRegularBreakPrices?.map((breakPrice, index) => {
                        const showRow = index > maxRows - 1 ? showAll : true;

                        if (!showRow) {
                            return;
                        }

                        return (
                            <DataTableRow
                                {...styles.tableRow}
                                key={breakPrice.breakQty}
                                data-test-selector="breakPriceRow"
                            >
                                <DataTableCell {...styles.cellQty} data-test-selector="breakQty">
                                    {breakPrice.breakQty}
                                </DataTableCell>
                                <DataTableCell {...styles.cellPrice} data-test-selector="price">
                                    {breakPrice.breakPriceDisplay}
                                </DataTableCell>
                                <DataTableCell {...styles.cellExtPrice} data-test-selector="extPrice">
                                    {getLocalizedCurrency({
                                        amount: extPrice(breakPrice.breakQty, breakPrice.breakPrice),
                                        language,
                                        currencySymbol,
                                    })}
                                </DataTableCell>
                            </DataTableRow>
                        );
                    })}
                </DataTableBody>
            </DataTable>

            {pricing?.unitRegularBreakPrices && pricing?.unitRegularBreakPrices.length > maxRows && (
                <Link {...styles.link} onClick={toggleShowAll}>
                    {translate(showAll ? "Hide Additional Prices" : "See Additional Prices")}
                </Link>
            )}
        </StyledWrapper>
    );
};

export default withProduct(CustomBreakPricing);
